import React from 'react';
import { useNavigate } from 'react-router-dom';
import EnhancedTable from '../enhanced-table/EnhancedTable';
import { EnhancedTableColumn, EnhancedTableColumnType, ITableMenuAction } from '../enhanced-table';
import { Button, Box } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { StorageUnitTableRow, StorageUnitDetailTableRow } from 'src/types/StorageUnit.types';

// Accepts either listing or detail table row type
type TableRow = StorageUnitTableRow | StorageUnitDetailTableRow;

interface StorageUnitTableProps {
  data: Record<string, TableRow>;
  loading?: boolean;
  actions?: ITableMenuAction[];
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  visibleColumns?: Array<keyof TableRow | 'actions'>;
}

const StorageUnitTable: React.FC<StorageUnitTableProps> = ({ data, loading, actions, onEdit, onDelete, visibleColumns }) => {
  const navigate = useNavigate();

  const isListing = Object.values(data)[0] && 'Id' in Object.values(data)[0];

  const columns: EnhancedTableColumn[] = isListing
    ? [
        {
          id: 'Code',
          label: 'Code',
          type: EnhancedTableColumnType.CLICKABLE,
          onClick: (id: string, row: StorageUnitTableRow) => navigate(`/storage-unit/${id}`),
        },
        { id: 'Name', label: 'Name', type: EnhancedTableColumnType.TEXT },
        { id: 'parentWarehouseName', label: 'Warehouse', type: EnhancedTableColumnType.TEXT },
        { id: 'parentStorageUnitName', label: 'Parent Storage Unit', type: EnhancedTableColumnType.TEXT },
        { id: 'Level', label: 'Level', type: EnhancedTableColumnType.TEXT },
        { id: 'UniqueProducts', label: 'Unique products', type: EnhancedTableColumnType.TEXT },
        { id: 'TotalQuantity', label: 'Total quantity', type: EnhancedTableColumnType.TEXT },
        {
          id: 'actions',
          label: 'Actions',
          type: EnhancedTableColumnType.ACTION,
          actions: [
            onEdit && {
              icon: <EditIcon fontSize="small" />,
              label: 'Edit',
              onClick: (id: string) => onEdit(id),
              visible: () => true,
            },
            onDelete && {
              icon: <DeleteIcon fontSize="small" />,
              label: 'Delete',
              onClick: (id: string) => onDelete(id),
              visible: () => true,
            },
          ].filter(Boolean),
        },
      ]
    : [
        {
          id: 'code',
          label: 'Code',
          type: EnhancedTableColumnType.CLICKABLE,
          onClick: (id: string, row: StorageUnitDetailTableRow) => navigate(`/storage-unit/${id}`),
        },
        { id: 'name', label: 'Name', type: EnhancedTableColumnType.TEXT },
        { id: 'parentWarehouseName', label: 'Warehouse', type: EnhancedTableColumnType.TEXT },
        { id: 'parentStorageUnitName', label: 'Parent Storage Unit', type: EnhancedTableColumnType.TEXT },
        { id: 'level', label: 'Level', type: EnhancedTableColumnType.TEXT },
        { id: 'UniqueProducts', label: 'Unique products', type: EnhancedTableColumnType.TEXT },
        { id: 'TotalQuantity', label: 'Total quantity', type: EnhancedTableColumnType.TEXT },
        {
          id: 'actions',
          label: 'Actions',
          type: EnhancedTableColumnType.ACTION,
          actions: [
            onEdit && {
              icon: <EditIcon fontSize="small" />,
              label: 'Edit',
              onClick: (id: string) => onEdit(id),
              visible: () => true,
            },
            onDelete && {
              icon: <DeleteIcon fontSize="small" />,
              label: 'Delete',
              onClick: (id: string) => onDelete(id),
              visible: () => true,
            },
          ].filter(Boolean),
        },
      ];

  const filteredColumns = visibleColumns
    ? columns.filter(col => visibleColumns.includes(col.id as keyof TableRow | 'actions'))
    : columns;

  return (
    <EnhancedTable
      title="Storage Units"
      columns={filteredColumns}
      data={data}
      loading={loading}
      actions={actions}
      wmsMode={true}
      striped={true}
    />
  );
};

export default StorageUnitTable; 